import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AppConfig } from '@/constants/residents/config';
import { logout } from '../slices/authSlice';
import { logoutAndClear } from '@/services/shared/auth/logoutAndClear';
import { performTokenRefresh } from '@/services/shared/auth/refreshTokenHelper';

// Error types for better error handling
export interface ApiError {
  status: number;
  data?: {
    message?: string;
    error?: string;
    details?: any;
  };
}

export interface NetworkError {
  name: string;
  message: string;
  stack?: string;
}

// Error status codes that should not show toast notifications
export const EXEMPTED_TOAST_STATUSES = [403, 401];

// Enhanced error logging function
const logApiError = (error: any, endpoint: string, method: string) => {
  console.group(`🔴 API Error - ${method.toUpperCase()} ${endpoint}`);
  
  if (error.status) {
    console.error('Status:', error.status);
    console.error('Data:', error.data);
  } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
    console.error('Network Error:', error.message);
    console.error('Possible causes: No internet, server down, CORS issues');
  } else {
    console.error('Unknown Error:', error);
  }
  
  console.groupEnd();
};

// Base query with authentication for RTK Query (admin)
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: AppConfig.API.BASE_URL,
  credentials: 'include',
  prepareHeaders: async (headers, { getState }) => {
    // Get token from secure storage
    const token = await SecureStorage.getInstance().getToken();
    // Respect pre-set Authorization header (e.g., Basic for admin v3 login)
    if (!headers.get('Authorization') && token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
  // Add timeout for requests
  // Note: Individual queries can override this (e.g., Google login uses 120 seconds)
  timeout: 30000, // 30 seconds default
});

// Enhanced base query with comprehensive error handling and automatic token refresh
export const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQueryWithAuth(args, api, extraOptions);
  
  // Extract endpoint info for logging
  const endpoint = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET');
  
  // Handle different types of errors
  if (result.error) {
    // Log the error for debugging
    logApiError(result.error, endpoint, method);
    
    const errorMessage = (result.error.data as any)?.error;

    // Unrecoverable 403 errors — token is fundamentally broken, refreshing won't help
    const FORCE_LOGOUT_ERRORS = ['Please sign in again.', 'invalid signature'];
    const shouldForceLogout = result.error.status === 403 && 
                              FORCE_LOGOUT_ERRORS.includes(errorMessage);

    if (shouldForceLogout) {
      await logoutAndClear(api.dispatch);
      return result;
    }

    // Handle token expiration (401 or 403 with jwt expired)
    const shouldRefresh = result.error.status === 401 || 
                         (result.error.status === 403 && errorMessage === 'jwt expired');
    
    if (shouldRefresh) {
      
      try {
        
        // Attempt token refresh (uses the same helper as BaseAPI)
        const refreshed = await performTokenRefresh();
        
        if (refreshed) {
          // Retry original request with new token
          result = await baseQueryWithAuth(args, api, extraOptions);
          if (result.error) {
            logApiError(result.error, endpoint, `${method} (retry after refresh)`);
          }
        } else {
          console.error('🔴 RTK Query: Token refresh failed, logging out');
          await logoutAndClear(api.dispatch);
          
          // Return a more user-friendly error
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: 'Session expired. Please log in again.' },
            },
          };
        }
      } catch (refreshError) {
        console.error('🔴 RTK Query: Token refresh error:', refreshError);
        await logoutAndClear(api.dispatch);
        
        return {
          error: {
            status: 'CUSTOM_ERROR',
            data: { message: 'Session expired. Please log in again.' },
          },
        };
      }
    }
    
    // Handle network errors
    else if (!result.error.status && 'name' in result.error && result.error.name === 'TypeError') {
      console.error('🔴 Network error detected');
      // Network errors will be handled by the component level error handling
    }
    
    // Handle server errors (5xx)
    else if (typeof result.error.status === 'number' && result.error.status >= 500) {
      console.error('🔴 Server error detected:', result.error.status);
      // Server errors will be handled by the component level error handling
    }
    
    // Handle client errors (4xx)
    else if (typeof result.error.status === 'number' && result.error.status >= 400 && result.error.status < 500) {
      console.error('🔴 Client error detected:', result.error.status);
      // Client errors will be handled by the component level error handling
    }
  }
  
  return result;
};
